import { TabItem } from "./types";

export const isSegmentMatch = (pathSegment: string, routeSegment: string): boolean => {
  if (routeSegment.startsWith(":")) {
    return true;
  }
  return pathSegment === routeSegment;
};

export const isTabActiveByUrl = (tab: TabItem, pathname: string): boolean => {
  if (tab.hasId) {
    return pathname.includes(tab.path ?? "");
  }

  if (tab.subTabs && tab.subTabs.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isSubTabActive = tab.subTabs.some((subTab: any) => {
      if (!subTab.path) {
        return false;
      }
      const pathSegments = pathname.split("/").filter(Boolean);
      const subTabSegments = subTab.path.split("/").filter(Boolean);

      if (pathSegments.length < subTabSegments.length) {
        return false;
      }

      return subTabSegments.every((segment: string, index: number) => {
        const pathIndex = pathSegments.length - subTabSegments.length + index;
        return pathIndex >= 0 && isSegmentMatch(pathSegments[pathIndex], segment);
      });
    });
    return isSubTabActive;
  }

  if (!tab.path) {
    return false;
  }
  const pathSegments = pathname.split("/").filter(Boolean);
  const tabSegments = tab.path.split("/").filter(Boolean);

  if (pathSegments.length < tabSegments.length) {
    return false;
  }

  return tabSegments.every((segment: string, index: number) => {
    const pathIndex = pathSegments.length - tabSegments.length + index;
    return pathIndex >= 0 && isSegmentMatch(pathSegments[pathIndex], segment);
  });
};

export const isSubTabActive = (subTab: TabItem, pathname: string): boolean => {
  if (!subTab.path) {
    return false;
  }

  if (subTab.hasId) {
    return pathname.startsWith(subTab.path);
  }

  const pathSegments = pathname.split("/").filter(Boolean);
  const subTabSegments = subTab.path.split("/").filter(Boolean);

  if (pathSegments.length < subTabSegments.length) {
    return false;
  }

  return subTabSegments.every((segment: string, index: number) => {
    const pathIndex = pathSegments.length - subTabSegments.length + index;
    return pathIndex >= 0 && isSegmentMatch(pathSegments[pathIndex], segment);
  });
};

export const getTabLinkPath = (tab: TabItem): string => {
  if (tab.subTabs && tab.subTabs.length > 0) {
    return `${tab.path}/${tab.subTabs[0].path}`;
  }
  return tab.path || "#";
};

export const getSelectedIndex = (
  tabs: TabItem[],
  dynamicTabs: TabItem[] | undefined,
  pathname: string,
): number => {
  const allTabs = [...tabs, ...(dynamicTabs || [])];
  const urlBasedIndex = allTabs.findIndex(tab => isTabActiveByUrl(tab, pathname));

  if (urlBasedIndex !== -1) {
    return urlBasedIndex;
  }
  return 0;
};

export const getCurrentTabPanelInfo = (
  tabs: TabItem[],
  dynamicTabs: TabItem[] | undefined,
  pathname: string,
): { title: string | null; description: string | null } => {
  const activeTabIndex = getSelectedIndex(tabs, dynamicTabs, pathname);
  const activeTab = [...tabs, ...(dynamicTabs || [])][activeTabIndex];

  if (!activeTab) {
    return { title: null, description: null };
  }

  if (activeTab.subTabs && activeTab.subTabs.length > 0) {
    const activeSubTab = activeTab.subTabs.find(subTab => isSubTabActive(subTab, pathname));

    if (activeSubTab) {
      return {
        title: activeSubTab.panelTitle || activeTab.panelTitle || null,
        description: activeSubTab.panelDescription || activeTab.panelDescription || null,
      };
    }
  }

  return {
    title: activeTab.panelTitle || null,
    description: activeTab.panelDescription || null,
  };
};
